import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * TablePagination — standardised pagination footer for all tables.
 *
 * Props:
 *   page             – current 1-based page number
 *   pageSize         – rows per page
 *   total            – total number of rows (after filtering)
 *   onPageChange     – (newPage: number) => void
 *   onPageSizeChange – (newSize: number) => void   (optional; hides selector if omitted)
 *   pageSizeOptions  – number[] (default [10, 25, 50, 100])
 *   className        – extra wrapper classes
 */
export default function TablePagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  className = '',
}) {
  if (!total) return null;

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = Math.min((page - 1) * pageSize + 1, total);
  const to = Math.min(page * pageSize, total);

  // Build page number buttons (max 5 visible with sliding window).
  const pageButtons = [];
  let start = Math.max(1, page - 2);
  let end = Math.min(totalPages, start + 4);
  if (end - start < 4) start = Math.max(1, end - 4);
  for (let i = start; i <= end; i++) pageButtons.push(i);

  return (
    <div
      className={`grid grid-cols-3 items-center gap-3 px-4 py-3 border-t border-gray-200 bg-gray-50 text-sm text-gray-600 ${className}`}
    >
      {/* Left: row info + page size */}
      <div className="flex items-center gap-3">
        <span>
          Showing{' '}
          <span className="font-medium text-gray-800">{from}</span>–
          <span className="font-medium text-gray-800">{to}</span> of{' '}
          <span className="font-medium text-gray-800">{total.toLocaleString()}</span>
        </span>
        {onPageSizeChange && (
          <div className="flex items-center gap-1.5">
            <span className="text-gray-400">|</span>
            <label className="text-gray-500">Rows:</label>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="border border-gray-300 rounded-md px-2 py-0.5 text-sm bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              {pageSizeOptions.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Center: page navigation — only shown when there's more than one page */}
      {totalPages > 1 ? (
        <div className="flex items-center justify-center gap-1">
          <button
            type="button"
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="p-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          {start > 1 && (
            <>
              <button
                type="button"
                onClick={() => onPageChange(1)}
                className="w-8 h-8 rounded-md border border-gray-300 bg-white hover:bg-gray-100 text-sm transition-colors"
              >
                1
              </button>
              {start > 2 && (
                <span className="px-1 text-gray-400">…</span>
              )}
            </>
          )}

          {pageButtons.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onPageChange(n)}
              className={`w-8 h-8 rounded-md text-sm font-medium transition-colors ${
                n === page
                  ? 'bg-indigo-600 text-white border border-indigo-600'
                  : 'border border-gray-300 bg-white hover:bg-gray-100 text-gray-700'
              }`}
            >
              {n}
            </button>
          ))}

          {end < totalPages && (
            <>
              {end < totalPages - 1 && (
                <span className="px-1 text-gray-400">…</span>
              )}
              <button
                type="button"
                onClick={() => onPageChange(totalPages)}
                className="w-8 h-8 rounded-md border border-gray-300 bg-white hover:bg-gray-100 text-sm transition-colors"
              >
                {totalPages}
              </button>
            </>
          )}

          <button
            type="button"
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            className="p-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="Next page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      ) : <div />}

      {/* Right: spacer for balance */}
      <div />
    </div>
  );
}
